import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, message, Typography } from 'antd';
import ResourceUploader from '../../../../components/Business/ResourceUploader';
import { orderAuditApi } from '../../../../services/process/order-audit';
import { OperationAction } from '../../../../types/process/operation-log';
import { MergedOrder } from '../../../../types/process/order';

const { Text } = Typography;

export interface SupplementMaterialModalProps {
  visible: boolean;
  order: MergedOrder | null;
  mode?: 'view' | 'edit';
  onCancel: () => void;
  onSuccess?: () => void;
}

const SupplementMaterialModal: React.FC<SupplementMaterialModalProps> = ({
  visible,
  order,
  mode = 'edit',
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // We support both simple array and named maps
  const requiredMaterials = useMemo(() => order?.audit?.requiredSupplementMaterials || [], [order]);
  const existingMaterials = useMemo(() => order?.audit?.supplementMaterials || [], [order]);
  const isMapFormat = requiredMaterials.length > 0;

  useEffect(() => {
    if (visible && order) {
      form.resetFields();
      if (!isMapFormat && Array.isArray(existingMaterials)) {
        form.setFieldsValue({ materials: existingMaterials });
      } else if (!Array.isArray(existingMaterials)) {
        form.setFieldsValue(existingMaterials);
      }
    }
  }, [visible, isMapFormat, existingMaterials, form, order]);

  const handleSubmit = async () => {
    if (!order) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      
      const payload = {
        audit: {
          supplementMaterials: isMapFormat ? values : (values.materials || []),
        }
      };

      await orderAuditApi.changeStatus(order.order.orderNo!, OperationAction.SubmitSupplement, payload);
      message.success('补充材料提交成功');
      onSuccess?.();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const formValues = Form.useWatch([], form);
  const uploadedCount = isMapFormat ? requiredMaterials.filter((m: string) => formValues?.[m] && formValues[m].length > 0).length : 0;

  if (!order) return null;

  const isView = mode === 'view';

  return (
    <Modal
      title={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>{isView ? '查看补充材料' : '上传补充材料'}</span>
          <span style={{ fontSize: 14, fontWeight: 400, color: '#666' }}>订单号: {order.order.orderNo}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={isMapFormat ? 800 : 520}
      destroyOnClose
      footer={
        isView ? null : [
          <Button key="cancel" onClick={onCancel} style={{ width: 80 }}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
            提交补充材料
          </Button>,
        ]
      }
    >
      {isMapFormat ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 500 }}>{isView ? '已上传材料：' : '请上传以下材料：'}</span>
            {!isView && <span style={{ fontSize: 14, color: '#666' }}>已上传 {uploadedCount} / {requiredMaterials.length}</span>}
          </div>

          <div style={{ background: '#fafafa', padding: 16, borderRadius: 8, maxHeight: '60vh', overflowY: 'auto' }}>
            <Form form={form} layout="vertical">
              {requiredMaterials.map((materialName: string, index: number) => (
                <div key={index} style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fa8c16' }}></div>
                    <span style={{ fontSize: 14, color: '#333' }}>{materialName}</span>
                  </div>
                  <Form.Item
                    name={materialName}
                    rules={isView ? [] : [{ required: true, message: `请上传${materialName}` }]}
                    style={{ marginBottom: 0 }}
                  >
                    <ResourceUploader maxCount={9} disabled={isView} />
                  </Form.Item>
                </div>
              ))}
            </Form>
          </div>
        </>
      ) : (
        <Form form={form} layout="vertical" preserve={false}>
          <div style={{ marginBottom: 16 }}>
            <Text strong>{isView ? '已补充的材料：' : '请上传补充材料：'}</Text>
          </div>
          <Form.Item
            name="materials"
            rules={isView ? [] : [{ required: true, message: '请上传补充材料' }]}
          >
            <ResourceUploader maxCount={9} disabled={isView} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default SupplementMaterialModal;
